import { useEffect, useState, type CSSProperties, type FormEvent } from 'react';
import { Card } from '../components/Card';
import { Rejection } from '../components/Rejection';
import { PageHeader } from '../components/Shell';
import { Waiting } from '../components/Waiting';
import { ApiError } from '../net/client';
import {
  expiresIn,
  invite,
  isPending,
  listAllInvitations,
  listAllMembers,
  placeholderFor,
  removeMember,
  teamOrder,
  type Invitation,
  type Member,
  type TeamRole,
} from '../net/team';
import { useSession, useSessionState } from '../session/useSession';
import { chip, colors, fonts, radius, shell, type, verdict } from '../theme';

/**
 * G9, `design/PortalTeam.dc.html`.
 *
 * Every page of members and of invitations, once. Send invitation and Resend
 * are both `POST /v1/organization/invitations`, since the API replaces an
 * open invitation to the same address. Remove asks the browser to confirm,
 * then `DELETE`s, and a refusal, the last admin's 409 among them, is the
 * form board's rejection box under the table. Every route is admin only, so
 * a member is told so and nothing is requested. D15.
 */

type Load =
  | { status: 'loading' }
  | { status: 'failed'; error: unknown }
  | { status: 'loaded'; members: Member[]; invitations: Invitation[] };

const roleLabels: Record<TeamRole, string> = { org_admin: 'Org admin', org_member: 'Member' };

/** The board's Member option, its em dash made a colon; the admin option in the header's own words. D15. */
const roleOptions: { value: TeamRole; label: string }[] = [
  { value: 'org_member', label: 'Member: view products & reports' },
  { value: 'org_admin', label: 'Org admin: manage products, codes, keys and the team' },
];

const roleTones: Record<TeamRole, { bg: string; fg: string }> = {
  org_admin: chip.brand,
  org_member: chip.neutral,
};

const columns = '44px 1.6fr 2fr 1fr 0.8fr';

export function Team() {
  const state = useSessionState();
  const self = state.status === 'signed_in' ? state.user : null;
  const isAdmin = self?.role === 'org_admin';
  const { api } = useSession();
  const [load, setLoad] = useState<Load>({ status: 'loading' });
  const [busy, setBusy] = useState(false);
  const [removeRefusal, setRemoveRefusal] = useState<string | null>(null);
  const [inviteRefusal, setInviteRefusal] = useState<string | null>(null);
  const [resendRefusal, setResendRefusal] = useState<string | null>(null);
  const [email, setEmail] = useState('');
  const [role, setRole] = useState<TeamRole>('org_member');

  useEffect(() => {
    if (!isAdmin) return;
    const controller = new AbortController();
    const { signal } = controller;
    Promise.all([listAllMembers(api, signal), listAllInvitations(api, signal)]).then(
      ([members, invitations]) => setLoad({ status: 'loaded', members, invitations }),
      (error: unknown) => {
        if (!signal.aborted) setLoad({ status: 'failed', error });
      },
    );
    return () => controller.abort();
  }, [api, isAdmin]);

  const forbidden =
    !isAdmin ||
    (load.status === 'failed' && load.error instanceof ApiError && load.error.kind === 'forbidden');
  if (self === null || forbidden) {
    return (
      <>
        <PageHeader title="Team" />
        <Card
          title="Team is for admins"
          body="Only organization admins can see and manage the team."
        />
      </>
    );
  }

  const now = new Date();
  const members = load.status === 'loaded' ? teamOrder(load.members, self.id) : [];
  const pending = load.status === 'loaded' ? load.invitations.filter((i) => isPending(i, now)) : [];

  /** A new invitation replaces any open one to the same address, as the API does. */
  const took = (invitation: Invitation) =>
    setLoad((current) =>
      current.status === 'loaded'
        ? {
            ...current,
            invitations: [
              invitation,
              ...current.invitations.filter((i) => !(i.open && i.email === invitation.email)),
            ],
          }
        : current,
    );

  const remove = async (member: Member) => {
    if (!window.confirm(`Remove ${member.name} from the team?`)) return;
    setBusy(true);
    setRemoveRefusal(null);
    try {
      await removeMember(api, member.id);
      setLoad((current) =>
        current.status === 'loaded'
          ? { ...current, members: current.members.filter((m) => m.id !== member.id) }
          : current,
      );
    } catch (error) {
      setRemoveRefusal(failureText(error));
    }
    setBusy(false);
  };

  const send = async (event: FormEvent) => {
    event.preventDefault();
    setBusy(true);
    setInviteRefusal(null);
    try {
      took(await invite(api, email, role));
      setEmail('');
    } catch (error) {
      setInviteRefusal(failureText(error));
    }
    setBusy(false);
  };

  const resend = async (invitation: Invitation) => {
    setBusy(true);
    setResendRefusal(null);
    try {
      took(await invite(api, invitation.email, invitation.role));
    } catch (error) {
      setResendRefusal(failureText(error));
    }
    setBusy(false);
  };

  return (
    <>
      {load.status === 'loading' ? <Waiting>Loading your team</Waiting> : null}
      <PageHeader
        title="Team"
        {...(load.status === 'loaded'
          ? {
              subtitle: `${members.length} ${members.length === 1 ? 'member' : 'members'} · admins manage products, codes, keys and the team`,
            }
          : {})}
      />
      {load.status === 'failed' ? (
        <Card title="The team could not be loaded" body={failureText(load.error)} />
      ) : (
        <div
          aria-busy={load.status === 'loading'}
          style={{
            display: 'grid',
            gridTemplateColumns: '1.8fr 1fr',
            gap: 16,
            alignItems: 'start',
          }}
        >
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            <div role="table" aria-label="Members" style={panel}>
              <div role="row" style={{ ...grid, borderBottom: `1px solid ${colors.line}` }}>
                <div role="columnheader" aria-label="Initial" />
                {['Name', 'Email', 'Role'].map((h) => (
                  <div
                    key={h}
                    role="columnheader"
                    style={{ ...type.tableHeader, color: colors.mutedInk }}
                  >
                    {h}
                  </div>
                ))}
                <div role="columnheader" aria-label="Action" />
              </div>
              {members.map((member, i) => (
                <div key={member.id}>
                  {i > 0 ? <div style={{ height: 1, background: colors.line }} /> : null}
                  <div role="row" style={{ ...grid, alignItems: 'center' }}>
                    <div role="cell" aria-hidden="true" style={avatar}>
                      {member.name.trim().charAt(0).toUpperCase()}
                    </div>
                    <div role="cell" style={{ ...type.bodyStrong, color: colors.navy }}>
                      {member.name}
                    </div>
                    <div role="cell" style={{ ...type.body, color: colors.mutedInk }}>
                      {member.email}
                    </div>
                    <div role="cell">
                      <span style={chipStyle(roleTones[member.role] ?? chip.neutral)}>
                        {roleLabels[member.role] ?? member.role}
                      </span>
                    </div>
                    <div role="cell">
                      {member.id === self.id ? (
                        <span style={{ ...type.metaBold, color: colors.mutedInk }}>You</span>
                      ) : (
                        <button
                          className="vp-link"
                          type="button"
                          disabled={busy}
                          aria-label={`Remove ${member.name}`}
                          onClick={() => void remove(member)}
                          style={{ ...linkButton, color: verdict.unknown.fg }}
                        >
                          Remove
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
            {removeRefusal !== null ? <Rejection>{removeRefusal}</Rejection> : null}
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            <form aria-label="Invite a teammate" onSubmit={(e) => void send(e)} style={panel}>
              <div style={{ padding: 22, display: 'flex', flexDirection: 'column', gap: 14 }}>
                <h2 style={panelHeading}>Invite a teammate</h2>
                <div style={fieldColumn}>
                  <label htmlFor="invite-email" style={fieldLabel}>
                    Email
                  </label>
                  <input
                    id="invite-email"
                    type="email"
                    value={email}
                    placeholder={placeholderFor(self.email)}
                    onChange={(e) => setEmail(e.target.value)}
                    style={input}
                  />
                </div>
                <div style={fieldColumn}>
                  <label htmlFor="invite-role" style={fieldLabel}>
                    Role
                  </label>
                  <div style={{ position: 'relative' }}>
                    <select
                      id="invite-role"
                      value={role}
                      onChange={(e) => setRole(e.target.value as TeamRole)}
                      style={{ ...input, appearance: 'none', paddingRight: 36, cursor: 'pointer' }}
                    >
                      {roleOptions.map((o) => (
                        <option key={o.value} value={o.value}>
                          {o.label}
                        </option>
                      ))}
                    </select>
                    <svg
                      width="14"
                      height="14"
                      viewBox="0 0 24 24"
                      fill="none"
                      aria-hidden="true"
                      style={{ position: 'absolute', right: 14, top: 15, pointerEvents: 'none' }}
                    >
                      <path
                        d="M6 9 L12 15 L18 9"
                        stroke={colors.mutedInk}
                        strokeWidth="2"
                        strokeLinecap="round"
                      />
                    </svg>
                  </div>
                </div>
                <button
                  className="vp-fill"
                  type="submit"
                  disabled={busy || load.status !== 'loaded'}
                  style={sendButton}
                >
                  Send invitation
                </button>
                <div style={{ ...type.caption, color: colors.mutedInk, lineHeight: 1.5 }}>
                  They'll get a single-use code by email, valid 7 days. Re-inviting replaces the old
                  code.
                </div>
                {inviteRefusal !== null ? <Rejection>{inviteRefusal}</Rejection> : null}
              </div>
            </form>
            <section aria-label="Pending invitations" style={panel}>
              <div
                style={{ padding: '18px 22px', display: 'flex', flexDirection: 'column', gap: 10 }}
              >
                <h2 style={panelHeading}>Pending invitations</h2>
                {load.status === 'loaded' && pending.length === 0 ? (
                  <div style={{ ...type.caption, color: colors.mutedInk }}>
                    No pending invitations.
                  </div>
                ) : null}
                {pending.map((invitation) => (
                  <div
                    key={invitation.id}
                    data-part="invitation"
                    style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      gap: 12,
                    }}
                  >
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                      <div style={{ ...type.bodyStrong, color: colors.navy }}>
                        {invitation.email}
                      </div>
                      <div style={{ ...type.caption, color: colors.mutedInk }}>
                        {`${roleLabels[invitation.role] ?? invitation.role} · ${expiresIn(invitation, now)}`}
                      </div>
                    </div>
                    <button
                      className="vp-link"
                      type="button"
                      disabled={busy}
                      aria-label={`Resend to ${invitation.email}`}
                      onClick={() => void resend(invitation)}
                      style={{ ...linkButton, color: colors.brand }}
                    >
                      Resend
                    </button>
                  </div>
                ))}
                {resendRefusal !== null ? <Rejection>{resendRefusal}</Rejection> : null}
              </div>
            </section>
          </div>
        </div>
      )}
    </>
  );
}

/** The API's field message when it names one, else its own message. */
function failureText(error: unknown): string {
  if (error instanceof ApiError && error.kind === 'network') {
    return 'The API could not be reached. Reload the page to try again.';
  }
  if (error instanceof ApiError && error.details !== undefined) {
    const fields = Object.values(error.details);
    if (fields.length > 0) return fields.join(' ');
  }
  if (error instanceof Error) return error.message;
  return 'Reload the page to try again.';
}

const panel: CSSProperties = {
  background: colors.card,
  border: `1px solid ${colors.line}`,
  borderRadius: radius.card,
  margin: 0,
};

const grid: CSSProperties = {
  display: 'grid',
  gridTemplateColumns: columns,
  gap: 14,
  padding: '13px 20px',
};

const avatar: CSSProperties = {
  width: 36,
  height: 36,
  borderRadius: 18,
  background: colors.navy,
  color: colors.white,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  fontFamily: fonts.display,
  fontWeight: 700,
  fontSize: '14px',
};

const chipStyle = (tone: { bg: string; fg: string }): CSSProperties => ({
  display: 'inline-flex',
  padding: '4px 12px',
  borderRadius: radius.chip,
  background: tone.bg,
  color: tone.fg,
  ...type.captionBold,
});

const linkButton: CSSProperties = {
  ...type.metaBold,
  background: 'none',
  border: 'none',
  padding: 0,
  cursor: 'pointer',
};

const panelHeading: CSSProperties = {
  fontFamily: fonts.display,
  fontWeight: 700,
  fontSize: '16px',
  color: colors.navy,
  margin: 0,
};

const fieldColumn: CSSProperties = { display: 'flex', flexDirection: 'column', gap: 6 };

const fieldLabel: CSSProperties = { ...type.statLabel, color: colors.mutedInk };

const input: CSSProperties = {
  height: 44,
  width: '100%',
  boxSizing: 'border-box',
  borderRadius: 12,
  background: colors.card,
  border: `1px solid ${colors.line}`,
  padding: '0 14px',
  ...type.body,
  color: colors.navy,
};

/** Drawn as a block in the form's column, so it spans the card with its label at the left. */
const sendButton: CSSProperties = {
  height: shell.actionButton.height,
  padding: `0 ${shell.actionButton.paddingX}px`,
  borderRadius: shell.actionButton.radius,
  display: 'flex',
  alignItems: 'center',
  width: '100%',
  background: colors.brand,
  color: colors.white,
  border: 'none',
  cursor: 'pointer',
  ...type.button,
};
